import * as React from 'react';
import {
  Badge,
  ComponentType,
  ContentLoader,
  Form,
  FormCheckboxGroup,
  FormText,
  FormTextField,
  Grid,
  isNullOrWhiteSpace,
  LoadingButton,
  requiredRule,
  Tooltip,
  UseFormReturn,
} from '@gcl/react-core';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { IUserProfileBase } from '@userProfile/userProfile.models';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { AxiosErrorResponse } from '@core/error.models';
import { IProjectTeam } from '@projectTeam/projectTeam.models';
import { IProjectRole, IProjectRoleOption } from '@role/projectRole/projectRole.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { useConfirmationModal } from '@common/confirmation.hooks';
import { useSearchUserInfoAsync } from '@user/user.queries';
import { IProjectCompany } from '@company/projectCompany/projectCompany.models';
import { useGetRoleOptionsForProjectOrProjectRequest } from '@role/projectRole/projectRole.queries';
import { ALL_USERS_TEAM_NAME, FETCH_QUERIES_NAME } from '@common/constants';
import { EMemberFormKeys, IMemberFormState } from './memberModal.models';
import './MemberForm.scss';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import {
  useLockProjectUserProfileAsync,
  useUnlockProjectUserProfileAsync,
} from '@userProfile/projectUserProfile.queries';
import usePrompt from '@common/prompt.hooks';
import { IUserInfo } from '@user/user.models';
import { useSyncTrainingRecordsByUser, useSyncUserProfileAsync } from '@userProfile/userProfile.queries';
import { useAppConfigs, useHasPermission } from '@config/config.hooks';
import { useQueryClient } from '@tanstack/react-query';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { EOrganizationTypeId } from '@company/organizationType/organizationType.constants';
import { ERolePermission } from '@userProfile/permission/permission.constants';

export interface IMemberFormProps {
  projectId?: number;
  projectUserProfile?: IProjectUserProfile;
  company?: IProjectCompany;
  teams?: IProjectTeam[] | undefined;
  rolesWithUser: IProjectRole[] | undefined;
  useFormMethods: UseFormReturn;
  errorUpdateProjectUserProfile?: AxiosErrorResponse | null;
  errorCreateProjectUserProfile?: AxiosErrorResponse | null;
  removeUserFromCompany: (companyId: number | undefined, projectUserProfileId: number | undefined) => void;
  isRemoveFromCompanyLoading?: boolean;
  isRemoveFromCompanyError?: boolean;
  isRemoveFromCompanySuccess?: boolean;
  onUserLockUnlockSuccess?: () => void;
  isShowTeams?: boolean;
  isShowLockProjectUser?: boolean;
  isShowSyncProfile?: boolean;
  isShowSyncTraining?: boolean;
  setIsFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
  type?: 'user' | 'projectRequest';
  shouldIncludeHiddenRoles?: boolean;
  selectedCompanyId?: number;
}

export const MemberForm: React.FunctionComponent<IMemberFormProps> = ({
  projectId,
  projectUserProfile,
  company,
  teams = [],
  rolesWithUser = [],
  useFormMethods,
  errorUpdateProjectUserProfile = null,
  errorCreateProjectUserProfile = null,
  removeUserFromCompany,
  isRemoveFromCompanyLoading = false,
  isRemoveFromCompanyError = false,
  isRemoveFromCompanySuccess = false,
  onUserLockUnlockSuccess,
  isShowTeams = true,
  isShowLockProjectUser = true,
  isShowSyncProfile = true,
  isShowSyncTraining = false,
  setIsFormDirty,
  type = 'user',
  shouldIncludeHiddenRoles = false,
  selectedCompanyId,
}) => {
  const appConfigs = useAppConfigs();
  const queryClient = useQueryClient();

  const { formState } = useFormMethods;
  const { isDirty, dirtyFields } = formState;

  React.useEffect(() => {
    setIsFormDirty(isDirty);
    return () => {
      setIsFormDirty(false);
    };
  }, [isDirty]);

  const USER_IS_LOCKED_ERROR_MSG = 'User is locked for this project, not allow to edit';
  const userSearchKeyword = useFormMethods.watch(EMemberFormKeys.USER_SEARCH_KEYWORD);
  const isExternal = useFormMethods.watch(EMemberFormKeys.IS_EXTERNAL);
  const tel = useFormMethods.watch(EMemberFormKeys.TEL);

  const [isLockOrUnlock, setIsLockOrUnlock] = React.useState<boolean>(false);
  const [localUserProfile, setLocalUserProfile] = React.useState<IUserProfileBase | undefined>(
    projectUserProfile?.userProfile || undefined,
  );
  const isUpdate = isPositiveNumber(projectUserProfile?.userProfile?.id);

  const hasUserRightAdminRight = useHasPermission(
    ERolePermission.ADMINISTRATOR,
    ECompanyTypeIdConst.SYSTEM_USERS,
    EOrganizationTypeId.SYS,
  );

  const {
    mutate: mutateLockProjectUser,
    isLoading: isLoadingLockProjectUser,
    reset: resetLockProjectUser,
    isSuccess: isSuccessLockProjectUser,
    isError: isErrorLockProjectUser,
    error: errorLockProjectUser,
    variables: variablesLockProjectUser,
  } = useLockProjectUserProfileAsync();

  const {
    mutate: mutateUnlockProjectUser,
    isLoading: isLoadingUnlockProjectUser,
    reset: resetUnlockProjectUser,
    isSuccess: isSuccessUnlockProjectUser,
    isError: isErrorUnlockProjectUser,
    error: errorUnlockProjectUser,
    variables: variablesUnlockProjectUser,
  } = useUnlockProjectUserProfileAsync();

  const {
    mutate: mutateSyncUserProfile,
    isLoading: isLoadingSyncUserProfile,
    isSuccess: isSuccessSyncUserProfile,
    reset: resetSyncUserProfile,
    isError: isErrorSyncUserProfile,
    error: errorSyncUserProfile,
    data: dataSyncUserProfile,
  } = useSyncUserProfileAsync();

  const {
    isLoading: isLoadingSyncTrainingRecordsByUser,
    isSuccess: isSuccessSyncTrainingRecordsByUser,
    isError: isErrorSyncTrainingRecordsByUser,
    error: errorSyncTrainingRecordsByUser,
    mutate: mutateSyncTrainingRecordsByUser,
    reset: resetSyncTrainingRecordsByUser,
  } = useSyncTrainingRecordsByUser();

  React.useEffect(() => {
    if (isSuccessLockProjectUser || isSuccessUnlockProjectUser) {
      if (onUserLockUnlockSuccess && typeof onUserLockUnlockSuccess === 'function') onUserLockUnlockSuccess();

      if (
        variablesLockProjectUser?.id === appConfigs.configs.myProjectUserProfile?.id ||
        variablesUnlockProjectUser?.id === appConfigs.configs.myProjectUserProfile?.id
      ) {
        queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.MY_DATA]);
      }
      resetLockProjectUser();
      resetUnlockProjectUser();
    }
  }, [isSuccessLockProjectUser || isSuccessUnlockProjectUser]);

  React.useEffect(() => {
    if (isSuccessLockProjectUser) resetSyncUserProfile();
  }, [isSuccessLockProjectUser]);

  const { renderModal, openPortal } = useConfirmationModal({
    content: !isLockOrUnlock
      ? `Confirm to Delete member ${`${projectUserProfile?.userProfile?.displayName || ''}`.trim()} from this company?`
      : `Confirm to ${projectUserProfile?.isLocked ? 'unlock' : 'lock'} for this project?`,
    onConfirm: () => {
      if (
        !isPositiveNumber(
          projectUserProfile?.id ?? projectUserProfile?.userProfileId ?? projectUserProfile?.userProfile?.id,
        )
      )
        return;

      if (!isLockOrUnlock) {
        if (removeUserFromCompany && typeof removeUserFromCompany === 'function')
          removeUserFromCompany(
            company?.id,
            type === 'user'
              ? projectUserProfile?.id
              : projectUserProfile?.userProfileId ?? projectUserProfile?.userProfile?.id,
          );
        return;
      }

      if (!projectUserProfile?.isLocked)
        mutateLockProjectUser({ id: projectUserProfile?.id ?? -1, projectId: projectUserProfile?.projectId ?? -1 });
      else
        mutateUnlockProjectUser({ id: projectUserProfile?.id ?? -1, projectId: projectUserProfile?.projectId ?? -1 });
    },
  });

  const {
    data: dataUserInfo,
    isLoading: isLoadingUserInfo,
    isError: isErrorUserInfo,
    isFetching: isFetchingUserInfo,
  } = useSearchUserInfoAsync(userSearchKeyword, projectId, !isUpdate);

  const { data: roleOptionsFromApi } = useGetRoleOptionsForProjectOrProjectRequest(
    {
      projectId,
      companyTypeId: company?.companyTypeId || 0,
      organizationTypeId: company?.organizationTypeId || 0,
      shouldIncludeHidden: shouldIncludeHiddenRoles,
    },
    !isDirty,
  );

  const roleOptions = React.useMemo(() => {
    return roleOptionsFromApi as IProjectRoleOption[];
  }, [JSON.stringify(roleOptionsFromApi)]);

  React.useEffect(() => {
    const state = getDefaultValues(
      isUpdate,
      localUserProfile,
      dataUserInfo,
      teams,
      company,
      roleOptions,
      rolesWithUser,
      userSearchKeyword,
    );
    useFormMethods.reset(state, { keepErrors: true, keepDirty: true });
  }, [JSON.stringify(roleOptions)]);

  const generateUserDetailTextGrid = (label: string, value?: any, size = 12) => (
    <Grid item xs={size}>
      <LabelWithMessage labelFor={label} text={label} />
      <div>{value || ''}</div>
    </Grid>
  );

  React.useEffect(() => {
    if (isUpdate || !dataUserInfo) return;
    const state = getDefaultValues(
      isUpdate,
      localUserProfile,
      dataUserInfo,
      teams,
      company,
      roleOptions,
      rolesWithUser,
      userSearchKeyword,
    );
    useFormMethods.reset(state, { keepErrors: true, keepDirty: true });
    useFormMethods.setValue(EMemberFormKeys.USER_PROFILE_ID, dataUserInfo?.userProfile?.id ?? null, {
      shouldDirty: false,
    });
  }, [JSON.stringify(dataUserInfo)]);

  const isResigned = projectUserProfile?.userProfile?.isResigned || dataUserInfo?.userProfile?.isResigned;

  const disableForm =
    isResigned ||
    projectUserProfile?.isLocked ||
    dataUserInfo?.projectUserProfile?.isLocked ||
    dataUserInfo?.userProfile?.isLocked;

  const getDefaultValues = (
    isUpdateLocal: boolean,
    localUserProfileLocal?: IUserProfileBase,
    dataUserInfoLocal?: IUserInfo,
    teamsLocal?: IProjectTeam[],
    companyLocal?: IProjectCompany,
    roleOptionsLocal?: IProjectRoleOption[],
    rolesWithUserLocal?: IProjectRole[],
    userSearchKeywordLocal?: string,
  ) => {
    if (!companyLocal || !roleOptionsLocal)
      return {
        [EMemberFormKeys.TEL]: null,
        [EMemberFormKeys.TEAMS]: null,
        [EMemberFormKeys.ROLES]: null,
        [EMemberFormKeys.COMPANY]: null,
        [EMemberFormKeys.PROJECT_USER_PROFILE]: null,
        [EMemberFormKeys.IS_EXTERNAL]: null,
        [EMemberFormKeys.USER_PROFILE]: null,
        [EMemberFormKeys.USER_INFO]: null,
        [EMemberFormKeys.USER_SEARCH_KEYWORD]: null,
      };
    // team
    const teamData =
      localUserProfileLocal?.upn || dataUserInfoLocal?.userPrincipalName
        ? (teamsLocal ?? []).filter((team) =>
            team.users?.some(
              (u) =>
                u.userProfile?.upn === localUserProfileLocal?.upn ||
                u.userProfile?.upn === dataUserInfoLocal?.userPrincipalName,
            ),
          ) ?? []
        : [];

    // roles
    const selectedRoles =
      (localUserProfileLocal || dataUserInfoLocal) &&
      (rolesWithUserLocal?.length ?? 0) > 0 &&
      (roleOptionsLocal?.length ?? 0) > 0
        ? (rolesWithUserLocal ?? [])
            .filter((roleWithUser) =>
              roleWithUser.users?.some((u) =>
                (u.userProfile?.upn.length ?? 0) > 0
                  ? u.userProfile?.upn === localUserProfileLocal?.upn ||
                    u.userProfile?.upn === dataUserInfoLocal?.userPrincipalName
                  : u.userProfileId === localUserProfileLocal?.id ||
                    u.userProfileId === dataUserInfoLocal?.userProfile?.id,
              ),
            )
            .map((roleWithUser) => {
              return {
                id: roleWithUser.id,
                projectId: roleWithUser.projectId,
                companyTypeId: roleWithUser.companyTypeId,
                roleId: roleWithUser.roleId,
                name: roleWithUser.role?.name,
                type: roleWithUser.role?.type,
              } as IProjectRoleOption;
            })
        : [];

    const state: IMemberFormState = {
      [EMemberFormKeys.TEL]: localUserProfileLocal?.tel ?? null,
      [EMemberFormKeys.TEAMS]: teamData,
      [EMemberFormKeys.ROLES]: selectedRoles,
      [EMemberFormKeys.COMPANY]: companyLocal ?? null,
      [EMemberFormKeys.PROJECT_USER_PROFILE]: null,
      [EMemberFormKeys.IS_EXTERNAL]: localUserProfileLocal?.isExternal ?? false,
      [EMemberFormKeys.USER_PROFILE]: null,
      [EMemberFormKeys.USER_INFO]: null,
      [EMemberFormKeys.USER_SEARCH_KEYWORD]: userSearchKeywordLocal ?? null,
    };

    if (isUpdateLocal) return state;

    let createState: IMemberFormState = {
      ...state,
    };

    // create mode
    if (!isNullOrWhiteSpace(userSearchKeywordLocal) && !dataUserInfoLocal) {
      useFormMethods.setError(EMemberFormKeys.USER_SEARCH_KEYWORD, { message: 'User not found' });
      return { ...state, [EMemberFormKeys.USER_INFO]: undefined };
    }

    useFormMethods.clearErrors();

    if (dataUserInfoLocal?.projectUserProfile && dataUserInfoLocal?.projectUserProfile.isLocked) {
      useFormMethods.setError(EMemberFormKeys.USER_SEARCH_KEYWORD, { message: USER_IS_LOCKED_ERROR_MSG });
    }

    createState = {
      ...createState,
      [EMemberFormKeys.PROJECT_USER_PROFILE]: dataUserInfo?.projectUserProfile ?? null,
      [EMemberFormKeys.IS_EXTERNAL]: dataUserInfo?.isExternal || false,
    };

    if (dataUserInfo?.userProfile) {
      createState = {
        ...createState,
        [EMemberFormKeys.USER_PROFILE]: dataUserInfo?.userProfile ?? null,
      };
      setLocalUserProfile(dataUserInfo.userProfile);
      return createState;
    }

    setLocalUserProfile(undefined);
    // useFormMethods.setValue(EMemberFormKeys.USER_PROFILE, undefined, { shouldDirty: false });
    // useFormMethods.setValue(EMemberFormKeys.USER_INFO, dataUserInfo, { shouldDirty: false });
    let tempTel = '';
    if (dataUserInfo) {
      if (dataUserInfo.mobilePhone) tempTel = dataUserInfo.mobilePhone;
      else if (dataUserInfo.businessPhones && dataUserInfo.businessPhones.length > 0)
        tempTel = dataUserInfo.businessPhones[0] || '';
    }
    // useFormMethods.setValue(EMemberFormKeys.TEL, tempTel || '', { shouldDirty: false });

    createState = {
      ...createState,
      [EMemberFormKeys.USER_PROFILE]: null,
      [EMemberFormKeys.USER_INFO]: dataUserInfo ?? null,
      [EMemberFormKeys.TEL]: tempTel || null,
    };

    return state;
  };

  const isShowLoader = !isUpdate && ((isLoadingUserInfo && !dataUserInfo) || isFetchingUserInfo);

  const fullName = React.useMemo(() => {
    if (isUpdate) {
      const profile = dataSyncUserProfile ?? projectUserProfile?.userProfile;
      return profile?.displayName;
    }

    const name = dataUserInfo?.displayName;
    if (!isNullOrWhiteSpace(name)) return name;
    return dataUserInfo?.displayName;
  }, [isUpdate, JSON.stringify(projectUserProfile), JSON.stringify(dataUserInfo), JSON.stringify(dataSyncUserProfile)]);

  return (
    <>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessSyncTrainingRecordsByUser,
            message: 'Sync training record success',
            reset: resetSyncTrainingRecordsByUser,
          },
        ]}
        callbackAfterToasted={() => {
          if (!isSuccessSyncTrainingRecordsByUser || !selectedCompanyId) return;
          queryClient.invalidateQueries([
            appConfigs.configs.project?.id,
            selectedCompanyId,
            FETCH_QUERIES_NAME.TEAMS_WITH_USER,
          ]);
          queryClient.invalidateQueries([
            appConfigs.configs.project?.id,
            selectedCompanyId,
            FETCH_QUERIES_NAME.ROLES_WITH_USER,
          ]);
        }}
      />
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          { error: errorCreateProjectUserProfile, genericError: 'Create user failed' },
          { error: errorUpdateProjectUserProfile, genericError: 'Update user failed' },
          { error: errorLockProjectUser, genericError: 'Lock user failed' },
          { error: errorUnlockProjectUser, genericError: 'Unlock user failed' },
          { error: errorSyncUserProfile, genericError: 'Sync user profile failed', reset: resetSyncUserProfile },
          {
            error: errorSyncTrainingRecordsByUser,
            genericError: 'Sync training record fail',
            reset: resetSyncTrainingRecordsByUser,
          },
        ]}
      />
      {renderModal(false, false, false)}
      <Form
        defaultValues={{
          callback: () => {
            const state = getDefaultValues(
              isUpdate,
              localUserProfile,
              dataUserInfo,
              teams,
              company,
              roleOptions,
              rolesWithUser,
              userSearchKeyword,
            );
            return state;
          },
          deps: [
            isUpdate,
            localUserProfile,
            dataUserInfo,
            teams,
            company,
            roleOptions,
            rolesWithUser,
            userSearchKeyword,
          ],
        }}
        enableUnsavedChangesPrompt
        unsavedChangesPromptProps={{
          usePrompt,
        }}
        useFormMethods={useFormMethods}
        render={(formFieldMethods) => {
          return (
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              {!isUpdate ? (
                <Grid item xs={12}>
                  <LabelWithMessage
                    labelFor={EMemberFormKeys.USER_SEARCH_KEYWORD}
                    text="Login Id / Email"
                    required={!isPositiveNumber(projectUserProfile?.userProfile?.id)}
                  />
                  <FormTextField
                    formFieldMethods={formFieldMethods}
                    id={EMemberFormKeys.USER_SEARCH_KEYWORD}
                    shouldDebounce
                    rules={{
                      ...requiredRule,
                      validate: (value: any) => {
                        if (dataUserInfo?.projectUserProfile?.isLocked) return USER_IS_LOCKED_ERROR_MSG;
                        if (!isNullOrWhiteSpace(value) && !dataUserInfo) return 'User not found';
                        return true;
                      },
                    }}
                  />
                </Grid>
              ) : undefined}

              {isUpdate || (!isNullOrWhiteSpace(userSearchKeyword) && !isErrorUserInfo) ? (
                <>
                  {isShowLoader && (
                    <Grid item xs={12}>
                      <ContentLoader
                        active
                        linePadding={10}
                        paragraphConfigs={[{ suffixBlankRow: false, numberOfRows: 5, lineHeight: 10 }]}
                        className="gcl-gems-member-form-content-loader"
                      />
                    </Grid>
                  )}

                  {!isShowLoader && (projectUserProfile || dataUserInfo) && (
                    <>
                      {generateUserDetailTextGrid(
                        'Full Name',
                        // eslint-disable-next-line no-nested-ternary
                        isUpdate ? (
                          <span>
                            {fullName}
                            {projectUserProfile?.isLocked ? (
                              <>
                                {' '}
                                <span className="gcl-gems-member-form-locked-label">[Locked]</span>
                              </>
                            ) : undefined}
                            {isResigned && (
                              <>
                                {' '}
                                <span className="gcl-gems-member-form-locked-label">[Resigned]</span>
                              </>
                            )}
                          </span>
                        ) : (
                          <span>
                            {fullName}
                            {isResigned && (
                              <>
                                {' '}
                                <span className="gcl-gems-member-form-locked-label">[Resigned]</span>
                              </>
                            )}
                          </span>
                        ),
                        isUpdate ? 6 : 12,
                      )}
                      {isUpdate ? (
                        <Grid item xs={6} className="gcl-gems-member-form-remove-button-container">
                          {isShowSyncTraining && hasUserRightAdminRight && !isResigned && (
                            <Tooltip
                              overlay="Sync Training Record"
                              id="gcl-gems-member-form-sync-training-record-tooltip"
                            >
                              <LoadingButton
                                small
                                title="Sync Training Record"
                                type={ComponentType.Success}
                                className="gcl-gems-member-form-remove-button"
                                onClick={(e) => {
                                  mutateSyncTrainingRecordsByUser({
                                    adUserName:
                                      projectUserProfile?.userProfile?.upn ?? dataUserInfo?.userProfile?.upn ?? '',
                                  });
                                }}
                                prefixIconName="ArrowClockwise"
                                isLoading={isLoadingSyncTrainingRecordsByUser}
                                isError={isErrorSyncTrainingRecordsByUser}
                                isSuccess={isSuccessSyncTrainingRecordsByUser}
                                timeToResetSuccessState={3000}
                                timeToResetErrorState={5000}
                              >
                                Sync Training Record
                              </LoadingButton>
                            </Tooltip>
                          )}
                          {isShowSyncProfile && !isResigned && (
                            <Tooltip overlay="Sync User Profile" id="gcl-gems-member-form-sync-member-profile-tooltip">
                              <LoadingButton
                                small
                                type={ComponentType.Info}
                                className="gcl-gems-member-form-remove-button"
                                onClick={(e) => {
                                  mutateSyncUserProfile({
                                    uid: projectUserProfile?.userProfile?.uid ?? dataUserInfo?.userProfile?.uid,
                                    isExternal,
                                  });
                                }}
                                prefixIconName="ArrowClockwise"
                                isLoading={isLoadingSyncUserProfile}
                                isError={isErrorSyncUserProfile}
                                isSuccess={isSuccessSyncUserProfile}
                                timeToResetSuccessState={3000}
                                timeToResetErrorState={5000}
                              >
                                Sync User Profile
                              </LoadingButton>
                            </Tooltip>
                          )}
                          {!projectUserProfile?.isLocked ? (
                            <Tooltip
                              overlay="Remove Member From Company"
                              id="gcl-gems-member-form-remove-member-from-company-tooltip"
                            >
                              <LoadingButton
                                type={ComponentType.Warning}
                                className="gcl-gems-member-form-remove-button"
                                data-testid="gcl-gems-member-form-remove-member-from-company-button"
                                onClick={(e) => {
                                  setIsLockOrUnlock(false);
                                  openPortal(e);
                                }}
                                prefixIconName="PersonDash"
                                isLoading={isRemoveFromCompanyLoading}
                                isError={isRemoveFromCompanyError}
                                isSuccess={isRemoveFromCompanySuccess}
                                timeToResetSuccessState={3000}
                                timeToResetErrorState={5000}
                              />
                            </Tooltip>
                          ) : undefined}
                          {isShowLockProjectUser && !isResigned && (
                            <Tooltip
                              overlay={`${projectUserProfile?.isLocked ? 'Unlock' : 'Lock'} Project Member`}
                              id="gcl-gems-member-form-lock-member-from-project-tooltip"
                            >
                              <LoadingButton
                                type={projectUserProfile?.isLocked ? ComponentType.Warning : ComponentType.Danger}
                                className="gcl-gems-member-form-remove-button"
                                onClick={(e) => {
                                  setIsLockOrUnlock(true);
                                  openPortal(e);
                                }}
                                prefixIconName={projectUserProfile?.isLocked ? 'Unlock' : 'Lock'}
                                isLoading={isLoadingLockProjectUser || isLoadingUnlockProjectUser}
                                isError={isErrorLockProjectUser || isErrorUnlockProjectUser}
                                isSuccess={isSuccessLockProjectUser || isSuccessUnlockProjectUser}
                                timeToResetSuccessState={3000}
                                timeToResetErrorState={5000}
                              />
                            </Tooltip>
                          )}
                        </Grid>
                      ) : undefined}
                      <Badge pill className="gcl-ml-sm gcl-mt-sm" type={ComponentType.Danger}>
                        {' '}
                        {isExternal ? 'External user' : 'Internal user'}
                      </Badge>
                      {generateUserDetailTextGrid('Company', company?.name)}
                      {generateUserDetailTextGrid(
                        'Email',
                        isUpdate
                          ? dataSyncUserProfile?.email ?? projectUserProfile?.userProfile?.email
                          : dataUserInfo?.mail,
                      )}
                      {generateUserDetailTextGrid('Tel', dataSyncUserProfile?.tel ?? tel ?? '-')}
                      <Grid item xs={12}>
                        <FormText isHidden formFieldMethods={formFieldMethods} id={EMemberFormKeys.USER_PROFILE_ID} />
                      </Grid>
                      <Grid item xs={12}>
                        <LabelWithMessage labelFor={EMemberFormKeys.ROLES} text="Roles" required />
                        <FormCheckboxGroup
                          itemPerRow={2}
                          allowSelectAll
                          checkOptions={roleOptions || []}
                          displayFieldName={(role) => {
                            return role.name || '';
                          }}
                          formFieldMethods={formFieldMethods}
                          id={EMemberFormKeys.ROLES}
                          checkIsSelected={(option1, option2) => {
                            return option1.projectId === option2.projectId && option1.id === option2.id;
                          }}
                          disableIndividualCheckbox={(option) => {
                            return !!option?.isReadOnly;
                          }}
                          disabled={disableForm}
                          defaultSortingKey="id"
                        />
                      </Grid>
                      {isShowTeams && (
                        <Grid item xs={12}>
                          <LabelWithMessage labelFor={EMemberFormKeys.TEAMS} text="Teams" />
                          <FormCheckboxGroup
                            itemPerRow={2}
                            allowSelectAll
                            checkOptions={(teams || []).filter((team) => team.teamName !== ALL_USERS_TEAM_NAME)}
                            displayFieldName="teamName"
                            formFieldMethods={formFieldMethods}
                            id={EMemberFormKeys.TEAMS}
                            checkIsSelected={(option1, option2) => {
                              return option1.projectId === option2.projectId && option1.id === option2.id;
                            }}
                            disabled={disableForm}
                            defaultSortingKey="id"
                          />
                        </Grid>
                      )}
                    </>
                  )}
                </>
              ) : undefined}
            </Grid>
          );
        }}
      />
    </>
  );
};
